import axios from 'axios';
import { getCachedData, setCachedData } from '../utils/apiCache';

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  withCredentials: true, // Send httpOnly cookies with every request
});

/**
 * Response error interceptor.
 *
 * - Silences 401 errors from /auth/me — these are expected for logged-out
 *   users and would otherwise spam the browser console on every page load.
 * - Re-throws all other errors unchanged so callers can handle them normally.
 */
API.interceptors.response.use(
  (response) => response,
  (error) => {
    const isSilentAuthCheck =
      error.config?.url?.includes('/auth/me') &&
      error.response?.status === 401;

    if (isSilentAuthCheck) {
      // Return a resolved promise with null to indicate "not authenticated"
      return Promise.resolve({ data: null, silent: true, status: 401 });
    }

    // Let non-auth-check errors surface normally
    return Promise.reject(error);
  }
);

// ─── Cache wrapper for GET requests ─────────────────────────────
// Returns cached data for 5 minutes, avoiding redundant API calls
// on repeat visits and page navigations.
const cachedGet = async (url, config = {}) => {
  const cacheKey = `${url}-${JSON.stringify(config.params || {})}`;

  // Check cache first
  const cached = getCachedData(cacheKey);
  if (cached && !config.skipCache) {
    return { data: cached, fromCache: true };
  }

  // Fetch from API
  const response = await API.get(url, config);
  setCachedData(cacheKey, response.data);
  return response;
};

// Auth (never cached — session-sensitive)
export const login = (data) => API.post('/auth/login', data);
export const register = (data) => API.post('/auth/register', data);
export const logout = () => API.post('/auth/logout');
export const getMe = async () => {
  try {
    const response = await API.get('/auth/me', {
      // Treat 401 as a valid response to prevent console errors
      validateStatus: (status) => status === 200 || status === 401,
    });
    
    // If 401, return null data
    if (response.status === 401) {
      return { data: null, status: 401, silent: true };
    }
    
    return response;
  } catch (error) {
    // Suppress any errors from this endpoint
    return { data: null, status: 401, silent: true };
  }
};

// Services — cached (public, read-heavy)
export const getServices = (params) => cachedGet('/services', { params });
export const getService = (slug) => cachedGet(`/services/${slug}`);
export const getServiceById = (id) => cachedGet(`/services/id/${id}`);
export const createService = (data) => API.post('/services', data);
export const updateService = (id, data) => API.put(`/services/id/${id}`, data);
export const deleteService = (id) => API.delete(`/services/id/${id}`);

// Projects — cached
export const getProjects = (params) => cachedGet('/projects', { params });
export const getProject = (slug) => cachedGet(`/projects/${slug}`);
export const getProjectCategories = () => cachedGet('/projects/categories/list');
export const createProject = (data) => API.post('/projects', data);
export const updateProject = (id, data) => API.put(`/projects/id/${id}`, data);
export const deleteProject = (id) => API.delete(`/projects/id/${id}`);

// Blogs — cached
export const getBlogs = (params) => cachedGet('/blogs', { params });
export const getBlog = (slug) => cachedGet(`/blogs/${slug}`);
export const createBlog = (data) => API.post('/blogs', data);
export const updateBlog = (id, data) => API.put(`/blogs/id/${id}`, data);
export const deleteBlog = (id) => API.delete(`/blogs/id/${id}`);

// Leads (write-heavy, no caching)
export const createLead = (data) => API.post('/leads', data);
export const getLeads = (params) => API.get('/leads', { params });
export const updateLead = (id, data) => API.put(`/leads/${id}`, data);
export const deleteLead = (id) => API.delete(`/leads/${id}`);
export const generateWhatsApp = (data) => API.post('/leads/whatsapp', data);

// Case Studies — cached
export const getCaseStudies = (params) => cachedGet('/case-studies', { params });
export const getCaseStudy = (slug) => cachedGet(`/case-studies/${slug}`);
export const createCaseStudy = (data) => API.post('/case-studies', data);
export const updateCaseStudy = (id, data) => API.put(`/case-studies/id/${id}`, data);
export const deleteCaseStudy = (id) => API.delete(`/case-studies/id/${id}`);

// Jobs — cached
export const getJobs = (params) => cachedGet('/jobs', { params });
export const getJob = (id) => cachedGet(`/jobs/${id}`);
export const createJob = (data) => API.post('/jobs', data);
export const updateJob = (id, data) => API.put(`/jobs/${id}`, data);
export const deleteJob = (id) => API.delete(`/jobs/${id}`);
export const applyToJob = (id, data) => API.post(`/jobs/${id}/apply`, data);
export const getApplications = () => API.get('/jobs/applications');

// Testimonials — cached
export const getTestimonials = (params) => cachedGet('/testimonials', { params });
export const getTestimonial = (id) => cachedGet(`/testimonials/${id}`);
export const createTestimonial = (data) => API.post('/testimonials', data);
export const updateTestimonial = (id, data) => API.put(`/testimonials/${id}`, data);
export const deleteTestimonial = (id) => API.delete(`/testimonials/${id}`);

// About — cached
export const getAbout = () => cachedGet('/about');

// Settings — cached
export const getSettings = () => cachedGet('/settings');
export const updateSettings = (data) => API.put('/settings', data);

// Admin — protected dashboard stats endpoint (not cached — always fresh)
export const getDashboardStats = () => API.get('/admin/stats');

export default API;
